import { useEffect, useRef } from 'react';
import { PROJECTS } from '../../../routes/path';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../../contexts/account/AccountContexts';

const useProjectNavigate = (projectId: number) => {
  const navigate = useNavigate();
  const { projectResponse, refetchProjectsSilently } = useAccount();
  // Remembers the projectId we refetched for and the list snapshot at that moment. We only give up
  // (redirect) once the list has actually changed afterwards and the project is still missing — so a
  // refetch still in flight (or StrictMode's double effect run in dev) never bounces a valid project.
  const refetchedFor = useRef<{ id: number; list: typeof projectResponse } | null>(null);

  useEffect(() => {
    // Wait until the project list is loaded before deciding. Redirecting while projectResponse
    // is still null (e.g. during a context refetch that remounts this page) would bounce the
    // user to /projects even though their project exists.
    if (!projectResponse) {
      return;
    }
    if (isNaN(projectId)) {
      navigate(PROJECTS);
      return;
    }
    const projectsIds = projectResponse.map((project) => project.id);
    if (projectsIds.includes(projectId)) {
      return;
    }

    // Project missing from the (possibly stale) context list — e.g. just created by another member
    // of the same account, whose creation only refetched their own session. Refetch once, then
    // decide only after the refreshed list has arrived.
    const attempt = refetchedFor.current;
    if (attempt && attempt.id === projectId) {
      // Redirect only once the list reference actually changed (refetch landed) and it's still
      // absent. While the refetch is in flight the reference is unchanged, so we keep waiting.
      if (attempt.list !== projectResponse) {
        navigate(PROJECTS);
      }
      return;
    }

    refetchedFor.current = { id: projectId, list: projectResponse };
    refetchProjectsSilently();
  }, [projectResponse, projectId, navigate, refetchProjectsSilently]);
};

export default useProjectNavigate;
